import { TextField } from '@mui/material';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { ConfirmDeleteDialog } from '~/components/confirm-delete-dialog';
import { DetailedErrorDialog } from '~/components/detailed-error-dialog';
import type { GenericDetailedError } from '~/errors/GenericDetailedError';
import { DimensionsService, type TimeDimension } from '~/services/dimensions';
import { ExportService, type Version } from '~/services/exports';
import { VersionService } from '~/services/version';
import { Roles } from '~/types/types';

const useStyles = makeStyles()({
  heading: {
    marginTop: '16px',
  },

  createButton: {
    width: '100%',
    marginTop: '16px',
    marginBottom: '16px',
  },

  description: {
    marginTop: '16px',
    marginBottom: '16px',
  },

  timeDimensionSelect: {
    width: '100%',
  },
});

type VersionHandlerProps = {
  classes: ReturnType<typeof useStyles>['classes'];
};

interface VersionHandlerState {
  description: string;
  selectedTimeDimension: string;
  timeDimensions: TimeDimension[];
  versions: Version[];
  snackbar: { show: boolean; message: string };
}

export const VersionHandlerRoles: Readonly<Roles[]> = [Roles.Versioning];

class VersionHandler extends React.Component<VersionHandlerProps, VersionHandlerState> {
  confirmDeleteDialogRef: React.RefObject<ConfirmDeleteDialog>;
  errorDialogRef: React.RefObject<DetailedErrorDialog>;

  constructor(props: VersionHandlerProps) {
    super(props);
    this.confirmDeleteDialogRef = React.createRef<ConfirmDeleteDialog>();
    this.errorDialogRef = React.createRef<DetailedErrorDialog>();
    this.state = {
      description: '',
      selectedTimeDimension: '',
      timeDimensions: [],
      versions: [],
      snackbar: {
        show: false,
        message: '',
      },
    };
  }

  async componentDidMount() {
    this.setState({
      timeDimensions: await DimensionsService.getSubmissionYears(),
    });
  }

  handleCreateButtonClick = () => {
    if (this.state.selectedTimeDimension !== '') {
      const timeObj = this.state.timeDimensions.find((x) => x.name === this.state.selectedTimeDimension);
      if (timeObj != null) {
        VersionService.NewRevision(timeObj.id, this.state.description);
        this.FreezeStarted();
      }
    }
  };

  FreezeStarted = () => {
    this.setState({
      snackbar: { show: true, message: 'frysning påbörjad.' },
    });
  };

  dialogError = (error: GenericDetailedError) => {
    if (this.errorDialogRef.current) {
      this.errorDialogRef.current.openDialog(error);
    }
  };
  handleTimeDimensionChange = (event: SelectChangeEvent<string>) => {
    this.setState({ selectedTimeDimension: event.target.value });
  };

  handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ description: event.target.value });
  };

  componentDidUpdate = async (prevProps: VersionHandlerProps, prevState: VersionHandlerState) => {
    if (prevState.selectedTimeDimension !== this.state.selectedTimeDimension) {
      const versions = await ExportService.getTimeDimensionVersions(this.state.selectedTimeDimension);
      versions.sort((a, b) => a.id - b.id);
      this.setState({ versions });
    }
  };

  render() {
    const { classes } = this.props;
    return (
      <>
        <Grid container item xs={12} md={10} lg={8} spacing={3}>
          <Grid item xs={12}>
            <Typography className={classes.heading} variant="h4">
              Versionshantering
            </Typography>
          </Grid>
          <Grid className={classes.description} item xs={12} md={10} lg={11}>
            <Typography>Välj vilket år du vill frysa:</Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <FormControl variant="standard" className={classes.timeDimensionSelect}>
              <InputLabel htmlFor="submission-year-picker">Submissionsår</InputLabel>
              <Select
                variant="standard"
                onChange={this.handleTimeDimensionChange}
                value={this.state.selectedTimeDimension}
                inputProps={{
                  name: 'year',
                  id: 'submission-year-picker',
                }}
              >
                {this.state.timeDimensions.map((td) => {
                  return (
                    <MenuItem value={td.name} key={td.id}>
                      {td.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
            <InputLabel htmlFor="submission-year-picker">Existerande revisioner</InputLabel>
            <Typography className={classes.timeDimensionSelect} data-testid="revision-list">
              {this.state.versions
                .filter((v) => v.name)
                .map((v) => v.name)
                .join(', ')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
            <Button
              className={classes.createButton}
              onClick={this.handleCreateButtonClick}
              variant="contained"
              color="primary"
              disabled={!this.state.selectedTimeDimension}
            >
              Frys revision
            </Button>
          </Grid>
          <Grid item xs={12} md={8} lg={10}>
            <FormControl variant="standard" className={classes.timeDimensionSelect}>
              <TextField
                variant="standard"
                className={classes.createButton}
                label={'Beskriving'}
                onChange={this.handleDescriptionChange}
                slotProps={{
                  htmlInput: {
                    name: 'description',
                    id: 'description-box',
                  },
                }}
                color="primary"
              >
                Frys revision
              </TextField>
            </FormControl>
          </Grid>
        </Grid>
        <DetailedErrorDialog ref={this.errorDialogRef} />
        <ConfirmDeleteDialog ref={this.confirmDeleteDialogRef} />
        <Snackbar
          open={this.state.snackbar.show}
          message={this.state.snackbar.message}
          autoHideDuration={6000}
          onClose={() => this.setState({ snackbar: { show: false, message: '' } })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />
      </>
    );
  }
}

const Wrapper = (props: Omit<VersionHandlerProps, 'classes'>) => {
  const { classes } = useStyles();

  return <VersionHandler classes={classes} {...props} />;
};

export default Wrapper;
